'use client'

import * as React from 'react'
import type { LucideIcon } from 'lucide-react'
import { useKeywordsStore } from '@/lib/store'
import { getCategoryOrder, getCategoryIcon } from '@/lib/keywords-service'
import { getIconComponent } from '@/lib/feature-icons'
import { localizeFeatureLabel } from '@/lib/feature-labels'
import { useI18n } from '@/lib/i18n'

// Feature keyword group derived from the keywords pipeline (useKeywordsStore).
export interface FeatureGroup {
  id: string
  label: string
  Icon: LucideIcon
  items: { id: string; label: string }[]
}

// The FULL feature catalogue grouped by category — extracted from FilterModal
// so the mobile search sheet renders the exact same set (one source of truth
// for "all available filters"): localized display names + labels, pipeline
// category order, icon per category. Falls back to cached filter tags when
// the categories payload hasn't loaded.
export function useFeatureGroups(): FeatureGroup[] {
  const { keywords, filterTags } = useKeywordsStore()
  const { uiLanguage } = useI18n()

  return React.useMemo<FeatureGroup[]>(() => {
    const skip = new Set(['rooms', 'price', 'area'])
    const order = getCategoryOrder()
    const orderIdx = (name: string) => {
      const i = order.indexOf(name)
      return i === -1 ? order.length : i
    }

    if (keywords?.categories && Object.keys(keywords.categories).length > 0) {
      return Object.entries(keywords.categories)
        .filter(([name]) => !skip.has(name))
        .sort((a, b) => orderIdx(a[0]) - orderIdx(b[0]))
        .map(([name, cat]) => ({
          id: name,
          label: cat.display_name || name,
          Icon: getIconComponent(getCategoryIcon(name)),
          items: cat.keywords.map((kw) => ({
            id: kw.id,
            label: localizeFeatureLabel(kw.feature_key, uiLanguage, kw),
          })),
        }))
        .filter((g) => g.items.length > 0)
    }

    // Fallback: group cached filter tags (static tags only cover rooms/price/area)
    const byCat: Record<string, { id: string; label: string }[]> = {}
    for (const tag of filterTags) {
      if (skip.has(tag.category)) continue
      ;(byCat[tag.category] ||= []).push({ id: tag.id, label: tag.label })
    }
    return Object.entries(byCat)
      .sort((a, b) => orderIdx(a[0]) - orderIdx(b[0]))
      .map(([name, items]) => ({
        id: name,
        label: name.replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase()),
        Icon: getIconComponent(getCategoryIcon(name)),
        items,
      }))
  }, [keywords, filterTags, uiLanguage])
}
